import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import {
  AdminBrandingService,
  AdminBranding,
  SaveAdminBrandingRequest,
} from "../../services/admin-branding.service";

/** Last applied API branding — restored on load to avoid empty-text flash before the API responds. */
export const ADMIN_BRANDING_CACHE_KEY = "saferoads-admin-branding-v1";

export function emptyAdminBranding(): SaveAdminBrandingRequest {
  return {
    programTitle: "",
    programName: "",
    portalLabel: "",
    tagline: "",
    sidebarTitle: "",
    sidebarSubtitle: "",
    sidebarTagline: "",
    toolbarTitle: "",
  };
}

function normalizeBranding(
  branding: AdminBranding | SaveAdminBrandingRequest
): SaveAdminBrandingRequest {
  return {
    programTitle: branding.programTitle ?? "",
    programName: branding.programName ?? "",
    portalLabel: branding.portalLabel ?? "",
    tagline: branding.tagline ?? "",
    sidebarTitle: branding.sidebarTitle ?? "",
    sidebarSubtitle: branding.sidebarSubtitle ?? "",
    sidebarTagline: branding.sidebarTagline ?? "",
    toolbarTitle: branding.toolbarTitle ?? "",
  };
}

export function readCachedAdminBranding(): SaveAdminBrandingRequest | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(ADMIN_BRANDING_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return normalizeBranding(parsed as SaveAdminBrandingRequest);
  } catch {
    return null;
  }
}

export function writeCachedAdminBranding(branding: SaveAdminBrandingRequest): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(ADMIN_BRANDING_CACHE_KEY, JSON.stringify(branding));
  } catch {
    // ignore quota / private mode
  }
}

@Injectable({
  providedIn: "root",
})
export class BrandingService {
  private readonly brandingSubject = new BehaviorSubject<SaveAdminBrandingRequest>(
    readCachedAdminBranding() ?? emptyAdminBranding()
  );
  private loadPromise: Promise<void> | null = null;

  readonly branding$ = this.brandingSubject.asObservable();

  constructor(private adminBrandingService: AdminBrandingService) {}

  getBranding(): SaveAdminBrandingRequest {
    return this.brandingSubject.value;
  }

  applyBranding(branding: AdminBranding | SaveAdminBrandingRequest | null | undefined): void {
    if (!branding) {
      this.brandingSubject.next(emptyAdminBranding());
      return;
    }
    const next = normalizeBranding(branding);
    this.brandingSubject.next(next);
    writeCachedAdminBranding(next);
  }

  /** Loads active branding once; concurrent callers share the same in-flight request. */
  loadAndApply(force = false): Promise<void> {
    if (!force && this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.fetchAndApply().finally(() => {
      this.loadPromise = null;
    });

    return this.loadPromise;
  }

  private async fetchAndApply(): Promise<void> {
    try {
      const branding = await firstValueFrom(this.adminBrandingService.getActive());
      this.applyBranding(branding);
    } catch (error) {
      console.warn("Admin branding unavailable; keeping cached branding if present.", error);
      // Do not clear to empty — cached text should remain until a successful load.
    }
  }
}
